/**
 * Others breakdown picker (2026-10-22) — clicking the "others" band of a wait-event chart opens this box.
 * It lists the waits currently drawn (pre-checked) and the waits folded into "others" with their share of the window,
 * and draws the checked ones as their own series (server param `events`). The rest stay in "others".
 * Server side: dbmsPerfRouter `eventsOf` · `othersListOf` (both /ash-trend and /history-wait-events).
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FiX } from 'react-icons/fi';
import './WaitOthersPicker.css';

/** The server draws at most this many picked waits (SEARCH_MAX) */
export const MAX_PICK = 12;
const EMPTY = [];

/** Initial checks — the current pick if any, otherwise what is drawn now (so ticking one more adds it to the view) */
export function initialChecks(drawn, picked) {
  return (picked && picked.length ? picked : drawn).slice(0, MAX_PICK);
}

function WaitOthersPicker({ open = false, drawn = EMPTY, othersList = EMPTY, picked = EMPTY, onApply, onReset, onClose, t }) {
  const [checked, setChecked] = useState(() => initialChecks(drawn, picked));
  // reset the checks each time the box opens — what is drawn may have changed since last time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setChecked(initialChecks(drawn, picked)); }, [open]);
  if (!open) return null;

  const toggle = (name) => setChecked(prev => (prev.includes(name)
    ? prev.filter(x => x !== name)
    : (prev.length >= MAX_PICK ? prev : [...prev, name])));
  const row = (name, extra) => (
    <label key={name} className="wop-row">
      <input type="checkbox" checked={checked.includes(name)} onChange={() => toggle(name)}
        disabled={!checked.includes(name) && checked.length >= MAX_PICK} />
      <span className="wop-name" title={name}>{name}</span>
      {extra}
    </label>
  );
  const folded = (othersList || []).filter(o => !drawn.includes(o.event));

  return (
    <div className="wop-box" role="dialog" aria-label={t('monitoring:perf.others.title')}>
      <div className="wop-head">
        <strong>{t('monitoring:perf.others.title')}</strong>
        <span className="wop-hint">{t('monitoring:perf.others.hint', { max: MAX_PICK })}</span>
        <button type="button" className="wop-close" onClick={onClose} aria-label={t('common:ui.close')} title={t('common:ui.close')}><FiX size={13} /></button>
      </div>
      <div className="wop-body">
        {drawn.length > 0 && (
          <div className="wop-group">
            <div className="wop-group-title">{t('monitoring:perf.others.drawn')}</div>
            {drawn.map(name => row(name, null))}
          </div>
        )}
        <div className="wop-group">
          <div className="wop-group-title">{t('monitoring:perf.others.folded')}</div>
          {folded.length === 0
            ? <div className="wop-empty">{t('monitoring:perf.others.empty')}</div>
            : folded.map(o => row(o.event, (
              <>
                {o.waitClass && <span className="wop-class">{o.waitClass}</span>}
                <span className="wop-share">{t('monitoring:perf.others.share', { v: o.share })}</span>
              </>
            )))}
        </div>
      </div>
      <div className="wop-foot">
        {picked && picked.length > 0 && (
          <button type="button" className="dbmsperf-btn dbmsperf-btn-secondary" onClick={onReset}>{t('monitoring:perf.others.reset')}</button>
        )}
        <span className="wop-count">{t('monitoring:perf.others.count', { n: checked.length, max: MAX_PICK })}</span>
        <button type="button" className="dbmsperf-btn" disabled={!checked.length} onClick={() => onApply(checked)}>
          {t('monitoring:perf.others.apply', { n: checked.length })}
        </button>
      </div>
    </div>
  );
}

WaitOthersPicker.propTypes = {
  open: PropTypes.bool,
  drawn: PropTypes.arrayOf(PropTypes.string),
  othersList: PropTypes.arrayOf(PropTypes.shape({ event: PropTypes.string, share: PropTypes.number, waitClass: PropTypes.string })),
  picked: PropTypes.arrayOf(PropTypes.string),
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default WaitOthersPicker;
